import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ZoomIn, ZoomOut } from 'lucide-react';
import { getAuthorizedBlobUrl } from 'services/storage-service';
import { useConfig } from 'hooks/use-config';
import { useTranslation } from 'react-i18next';

interface PDFViewerProps {
    url: string;
    setUrl: (url: string) => void;
    fileType?: string;
}

export default function PDFViewer({ url, setUrl, fileType }: PDFViewerProps) {
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        const fetchAuthorizedUrl = async () => {
            if (!url || !configurationSettings) {
                return null;
            }
            if (url.startsWith('blob:')) {
                return null;
            }

            setIsLoading(true);
            try {
                const authorizedUrl = await getAuthorizedBlobUrl(url, configurationSettings);
                if (!authorizedUrl) {
                    return null;
                }
                setUrl(authorizedUrl);
            } finally {
                setIsLoading(false);
            }
        }
        fetchAuthorizedUrl();
    }, [url, configurationSettings, setUrl]);
    const [showModal, setShowModal] = useState(true);
    const [scale, setScale] = useState(1.0);

    const handleClose = () => {
        setShowModal(false);
        setTimeout(() => setUrl(''), 200);
    };

    const handleZoomIn = () => setScale(prev => Math.min(prev + 0.2, 3.0));
    const handleZoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));

    if (!showModal) return null;

    const openPdfInNewTab = (pdfUrl: string) => {
        if (!pdfUrl) return;
        const encodedUrl = encodeURIComponent(url);
        const stringifiedUrl = JSON.stringify(encodedUrl);
        if (stringifiedUrl) {
            window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        }
        handleClose();
        return null;
    };

    return createPortal(
        <div
            className="pointer-events-auto fixed inset-0 z-[100] flex items-center justify-center px-3 sm:px-6 py-6" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
        >

            <div
                className="absolute inset-0 bg-[rgba(0,0,0,0.4)]"
                onClick={handleClose}
            />


            <div className="relative flex h-[90vh] max-h-[90vh] w-full max-w-[90vw] flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:h-full sm:max-h-[85vh] lg:max-w-[1400px]">


                <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-100">
                    {fileType === 'Photo' && (
                        <div className="flex items-center space-x-1 sm:space-x-2">
                            <button onClick={handleZoomOut} className="p-1.5 text-gray-400 hover:text-gray-600">
                                <ZoomOut className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                            <span className="text-xs sm:text-sm text-gray-500 min-w-[40px] text-center">
                                {Math.round(scale * 100)}%
                            </span>
                            <button onClick={handleZoomIn} className="p-1.5 text-gray-400 hover:text-gray-600">
                                <ZoomIn className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                        </div>
                    )}

                    <div className="flex items-center space-x-1 sm:space-x-2">
                        <a href={url} download className="p-1.5 text-gray-400 hover:text-gray-600">
                            <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                        </a>
                        <button onClick={handleClose} className="p-1.5 text-gray-400 hover:text-gray-600">
                            <X className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto p-3 sm:p-6 flex justify-center items-center">
                    {isLoading ? (
                        <div className="flex flex-col items-center gap-2 text-gray-500">
                            <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                            <span className="text-sm">{t('LOADING_PREVIEW', 'Loading preview…')}</span>
                        </div>
                    ) : fileType === 'Pdf' ? (
                        openPdfInNewTab(url)
                    ) : fileType === 'Photo' ? (
                        <img
                            src={url}
                            alt={t('PREVIEW', 'Preview')}
                            className="shadow-sm rounded object-contain max-w-full max-h-full"
                            style={{ transform: `scale(${scale})` }}
                        />
                    ) : null}
                </div>
            </div>
        </div>,
        document.body
    );
}
